import { line as d3Line } from 'd3-shape';

import {
  DEFAULT_DATA_POINT_RADIUS,
  DEFAULT_FILL_OPACITY,
  DEFAULT_SERIES_CURVE_SHAPE,
} from '../../../../constants.js';
import { OVERLAY_PADDING } from '../../../data-point/constants.js';
import {
  FnCanvasAccessor,
  LineCanvasInstance,
} from '../../types/canvas-shape.js';
import { getCanvasColor } from '../canvas-color.js';
import { mapSeriesCurveToD3 } from '../map-series-curve-to-d3.js';

type FnLineCanvasStyle<Datum> = (
  ctx: CanvasRenderingContext2D,
  lineCanvas: LineCanvasInstance<Datum>,
  x: FnCanvasAccessor<Datum>,
  y: FnCanvasAccessor<Datum>,
) => void;
/**
 * Builds style FN Line based on parameters.
 *
 * @param strokeWidth - stroke width
 * @param enableDotOverflow - enable dots indicators to be drawn overflowing the plot area
 * @param radius - scatter plot radius
 * @returns - fn print Line canvas
 */
export function printLineBuilder<Datum>(
  strokeWidth: number,
  enableDotOverflow: boolean,
  radius?: number,
): FnLineCanvasStyle<Datum> {
  const line = d3Line<Datum>();

  return (ctx, lineCanvas, x, y) => {
    const {
      color,
      data,
      opacity = DEFAULT_FILL_OPACITY,
      defined = () => true,
      displayScatterPlot,
      curve = DEFAULT_SERIES_CURVE_SHAPE,
    } = lineCanvas;

    const lineColor = getCanvasColor(color(data[0]));

    const lineCurve = mapSeriesCurveToD3(curve);

    ctx.save(); // save before applying any translation

    if (enableDotOverflow) {
      ctx.translate(OVERLAY_PADDING / 2, OVERLAY_PADDING / 2);
      ctx.save(); // save before doing clipping

      ctx.beginPath();
      ctx.rect(0, 0, lineCanvas.canvasWidth, lineCanvas.canvasHeight);
      ctx.clip();
    }

    ctx.beginPath();
    const linePath = line
      .defined(defined)
      .curve(lineCurve)
      .x(x)
      .y(y)
      .context(ctx);

    ctx.lineJoin = 'bevel';
    linePath(data);

    ctx.globalAlpha = opacity;
    ctx.lineWidth = strokeWidth;
    ctx.filter = 'none';

    ctx.strokeStyle = lineColor;
    ctx.stroke();

    if (enableDotOverflow) {
      ctx.restore();
    }

    if (displayScatterPlot) {
      ctx.beginPath();
      const dataLength = data.length;
      const scatterRadius = radius ?? DEFAULT_DATA_POINT_RADIUS;

      for (let i = 0; i < dataLength; i++) {
        if (
          !defined(data[i]) ||
          !displayScatterPlot(data, i) ||
          y(data[i], i) > lineCanvas.canvasHeight ||
          y(data[i], i) < 0 ||
          x(data[i], i) > lineCanvas.canvasWidth ||
          x(data[i], i) < 0
        ) {
          continue;
        }

        ctx.moveTo(x(data[i], i), y(data[i], i));
        ctx.arc(x(data[i], i), y(data[i], i), scatterRadius, 0, 2 * Math.PI);
      }

      ctx.filter = 'none';
      ctx.globalAlpha = opacity;
      ctx.fillStyle = lineColor;
      ctx.fill();
    }

    ctx.restore();
  };
}
